import React from "react";
import { lazy, Suspense } from "react";
import GlobalNav from "@/components/GlobalNav";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import { RecruiterAuthProvider } from "./contexts/RecruiterAuthContext";
import { RecruiterRedirectGuard } from "@/components/RecruiterRedirectGuard";
import { RequireAuth } from "@/components/RequireAuth";
import { RequireAdmin } from "@/components/RequireAdmin";
import { useAnalytics } from "./hooks/useAnalytics";
import { siteEvents, installSiteEvents } from "./lib/siteEvents";
// Side effect: keeps the alert email's ?aid= for checkout attribution.
import "./lib/alertRef";
import Layout from "./layouts/Layout";
import { ScrollToTop } from "./components/ScrollToTop";
import { RouteFallback } from "./components/RouteFallback";

// Eager: hot paths (home + job detail) — loaded synchronously.
import HomeV2 from "./pages/v2/HomeV2";
import JobDetailPage from "./pages/JobDetailPage";
import NotFound from "./pages/NotFound";

// Lazy: everything else.
const ToolLandingPage = lazy(() => import("./pages/v2/tools/ToolLandingPage"));
const ListLandingPage = lazy(() => import("./pages/v2/lists/ListLandingPage"));
const ListsHub = lazy(() => import("./pages/v2/lists/ListsHub"));
const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const AdminStatsHome = lazy(() => import("./pages/admin/AdminStatsHome"));
const AdminHealth = lazy(() => import("./pages/admin/AdminHealth"));
const AdminUsage = lazy(() => import("./pages/admin/AdminUsage"));
const NewslettersHub = lazy(() => import("./pages/admin/newsletters/NewslettersHub"));
const NewsletterDetail = lazy(() => import("./pages/admin/newsletters/NewsletterDetail"));
const NewsletterEditor = lazy(() => import("./pages/admin/newsletters/NewsletterEditor"));
const ContentStudio = lazy(() => import("./pages/admin/studio/ContentStudio"));
const InsightsConsole = lazy(() => import("./pages/admin/insights/InsightsConsole"));
const AdminLibraryVideos = lazy(() => import("./pages/admin/AdminLibraryVideos"));
const AllJobs = lazy(() => import("./pages/AllJobs"));
const JobDetailPreview = lazy(() => import("./pages/JobDetailPreview"));
const LinkedInOptimizer = lazy(() => import("./pages/LinkedInOptimizer"));
const ATSJobSearch = lazy(() => import("./pages/ATSJobSearch"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Auth = lazy(() => import("./pages/Auth"));
const SignIn = lazy(() => import("./pages/auth/SignIn"));
const SignUp = lazy(() => import("./pages/auth/SignUp"));
const CheckEmail = lazy(() => import("./pages/auth/CheckEmail"));
const AuthCallback = lazy(() => import("./pages/auth/AuthCallback"));
const VerifyEmail = lazy(() => import("./pages/auth/VerifyEmail"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/auth/ResetPassword"));
const About = lazy(() => import("./pages/about/About"));
const Candidates = lazy(() => import("./pages/Candidates"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const BriefingAnswer = lazy(() => import("./pages/BriefingAnswer"));
const PlaybookAccess = lazy(() => import("./pages/PlaybookAccess"));
const PlaybookPage = lazy(() => import("./pages/playbook/PlaybookPage"));
const LibraryLayout = lazy(() => import("./pages/library/LibraryLayout"));
const LibraryHome = lazy(() => import("./pages/library/LibraryHome"));
const LibraryPros = lazy(() => import("./pages/library/LibraryPros"));
const SmallBusiness = lazy(() => import("./pages/library/SmallBusiness"));
const LibraryItemPage = lazy(() => import("./pages/library/LibraryItemPage"));
const LibraryAccess = lazy(() => import("./pages/library/LibraryAccess"));
const LibraryUpgrade = lazy(() => import("./pages/library/LibraryUpgrade"));
const AiMonthEndClose = lazy(() => import("./pages/library/AiMonthEndClose"));
const YearEndForCpa = lazy(() => import("./pages/library/YearEndForCpa"));
const ClaudeSkillsForAccountants = lazy(() => import("./pages/library/ClaudeSkillsForAccountants"));
const TestEmail = lazy(() => import("./pages/TestEmail"));
const RecruiterSignIn = lazy(() => import("./pages/recruiter/sign-in/RecruiterSignIn"));
const SocialInsights = lazy(() => import("./pages/internal/SocialInsights"));
const BackfillLocations = lazy(() => import("./pages/internal/BackfillLocations"));
const JobIntelligence = lazy(() => import("./pages/internal/JobIntelligence"));
const JobListAdsAdmin = lazy(() => import("./pages/internal/JobListAdsAdmin"));
const NewsletterGenerator = lazy(() => import("./pages/internal/NewsletterGenerator"));
const BulkJobUpload = lazy(() => import("./pages/internal/BulkJobUpload"));
import { LANDING_PATHS } from "./seo/landingPages";
const BetaHome = lazy(() => import("./pages/beta/BetaHome"));
const BetaJobDetail = lazy(() => import("./pages/beta/BetaJobDetail"));
const GetStarted = lazy(() => import("./pages/GetStarted"));
const RecruiterLandingPage = lazy(() => import("./pages/recruiter-landing/RecruiterLandingPage"));
const RecruiterPost = lazy(() => import("./pages/recruiter/post/RecruiterPost"));
const RecruiterCheckout = lazy(() => import("./pages/recruiter/checkout/RecruiterCheckout"));
const RecruiterCheckoutSuccess = lazy(() => import("./pages/recruiter/checkout/RecruiterCheckoutSuccess"));
const RecruiterDashboardV1 = lazy(() => import("./pages/recruiter/dashboard/RecruiterDashboard"));
const RecruiterEditListing = lazy(() => import("./pages/recruiter/dashboard/RecruiterEditListing"));
const RecruiterSettingsV1 = lazy(() => import("./pages/recruiter/dashboard/RecruiterSettings"));
const RecruiterBillingV1 = lazy(() => import("./pages/recruiter/dashboard/RecruiterBilling"));
const Pricing = lazy(() => import("./pages/Pricing"));
const SprintLanding = lazy(() => import("./pages/sprint/SprintLanding"));
const SprintSuccess = lazy(() => import("./pages/sprint/SprintSuccess"));
const SprintSignals = lazy(() => import("./pages/sprint/SprintSignals"));
const DataHub = lazy(() => import("./pages/data/DataHub"));
const MarketExplorer = lazy(() => import("./pages/data/MarketExplorer"));
const DataMethodology = lazy(() => import("./pages/data/DataMethodology"));
const DataPress = lazy(() => import("./pages/data/DataPress"));
const DataLicense = lazy(() => import("./pages/data/DataLicense"));
const EmbedExplorer = lazy(() => import("./pages/data/EmbedExplorer"));
const EmbedPremiumJobs = lazy(() => import('./pages/data/EmbedPremiumJobs'));
const EarlyCareer = lazy(() => import('./pages/data/EarlyCareer'));
const StudentLanding = lazy(() => import('./pages/data/StudentLanding'));
const AudiencePage = lazy(() => import('./pages/data/AudiencePage'));
const SoftwareTracker = lazy(() => import('./pages/data/SoftwareTracker'));
const RtoTracker = lazy(() => import('./pages/data/RtoTracker'));
const HiringCalendar = lazy(() => import('./pages/data/HiringCalendar'));
const TimeToClose = lazy(() => import('./pages/data/TimeToClose'));
const VsRobertHalf = lazy(() => import('./pages/data/VsRobertHalf'));
const EmbedInternships = lazy(() => import('./pages/data/EmbedTables').then(m => ({ default: m.EmbedInternships })));
const AboveMarketFeed = lazy(() => import('./pages/data/AboveMarketFeed'));
const CpaFirmTracker = lazy(() => import('./pages/data/CpaFirmTracker'));
const CpaPremium = lazy(() => import('./pages/data/CpaPremium'));
// PROTOTYPE — design-system rebuild, noindex, not linked from anywhere.
const RemoteFirst = lazy(() => import('./pages/data/RemoteFirst'));
const CpaFirmJobs = lazy(() => import('./pages/data/CpaFirmJobs'));
const GoInterstitial = lazy(() => import('./pages/data/GoInterstitial'));
const Confirmed = lazy(() => import('./pages/data/Confirmed'));
const DataLive = lazy(() => import('./pages/data/DataLive'));
const CityPage = lazy(() => import('./pages/data/CityPage'));
const FpaPage = lazy(() => import('./pages/data/FpaPage'));
const RemoteCountryPage = lazy(() => import('./pages/data/RemoteCountryPage'));
const SkillsPage = lazy(() => import('./pages/data/SkillsPage'));
const RolePage = lazy(() => import('./pages/data/RolePage'));
const EntryLevel = lazy(() => import('./pages/data/EntryLevel'));
const OpenDoor = lazy(() => import('./pages/data/OpenDoor'));
const Credentials = lazy(() => import('./pages/data/Credentials'));
const CampusEmployerPage = lazy(() => import('./pages/data/CampusEmployer'));
const CareerCenters = lazy(() => import('./pages/data/CareerCenters'));
const CityOnePager = lazy(() => import('./pages/data/CityOnePager'));
const EmbedLive = lazy(() => import('./pages/data/EmbedLive'));
const EmbedRemoteWeekly = lazy(() => import('./pages/embed/EmbedRemoteWeekly'));
const EmbedNewsletterWidget = lazy(() => import('./pages/embed/nl/EmbedNewsletterWidget'));
const EmbedCallList = lazy(() => import('./pages/data/EmbedCallList'));
const LayoffTracker = lazy(() => import('./pages/data/LayoffTracker'));
const SunshineOntario = lazy(() => import('./pages/data/SunshineOntario'));
const PublicPayCompare = lazy(() => import('./pages/data/PublicPayCompare'));
const Curation = lazy(() => import('./pages/data/Curation'));
const EmbedCuration = lazy(() => import('./pages/data/EmbedTables').then(m => ({ default: m.EmbedCuration })));
const EmbedLayoffs = lazy(() => import('./pages/data/EmbedTables').then(m => ({ default: m.EmbedLayoffs })));
const EmbedAiCompanies = lazy(() => import('./pages/data/EmbedTables').then(m => ({ default: m.EmbedAiCompanies })));
const AiCompanies = lazy(() => import('./pages/data/AiCompanies'));
const EmbedPostingHygiene = lazy(() => import('./pages/data/EmbedTables').then(m => ({ default: m.EmbedPostingHygiene })));
const PostingHygiene = lazy(() => import('./pages/data/PostingHygiene'));
const Updates = lazy(() => import('./pages/updates/Updates'));
const LearnConferences = lazy(() => import('./pages/learn/Conferences'));
const LearnCpe = lazy(() => import('./pages/learn/Cpe'));
const EmbedCpaPremium = lazy(() => import('./pages/data/EmbedTables').then(m => ({ default: m.EmbedCpaPremium })));
const EmbedRemoteFirst = lazy(() => import('./pages/data/EmbedTables').then(m => ({ default: m.EmbedRemoteFirst })));
const EmbedCpaFirmPay = lazy(() => import('./pages/data/EmbedTables').then(m => ({ default: m.EmbedCpaFirmPay })));
const EmbedCpaFirmHiring = lazy(() => import('./pages/data/EmbedTables').then(m => ({ default: m.EmbedCpaFirmHiring })));
const EmbedCpaFirms = lazy(() => import('./pages/data/EmbedTables').then(m => ({ default: m.EmbedCpaFirms })));
const EmbedSoftware = lazy(() => import('./pages/data/EmbedTables').then(m => ({ default: m.EmbedSoftware })));
const EmbedRto = lazy(() => import('./pages/data/EmbedTables').then(m => ({ default: m.EmbedRto })));
const EmbedTimeToClose = lazy(() => import('./pages/data/EmbedTables').then(m => ({ default: m.EmbedTimeToClose })));
const EmbedHiringCalendar = lazy(() => import('./pages/data/EmbedTables').then(m => ({ default: m.EmbedHiringCalendar })));
const EmbedVsRobertHalf = lazy(() => import('./pages/data/EmbedTables').then(m => ({ default: m.EmbedVsRobertHalf })));
const EmbedPublicPayCompare = lazy(() => import('./pages/data/EmbedCharts').then(m => ({ default: m.EmbedPublicPayCompare })));
const EmbedProvincePay = lazy(() => import('./pages/data/EmbedCharts').then(m => ({ default: m.EmbedProvincePay })));
const EmbedEntryLevel = lazy(() => import('./pages/data/EmbedCharts').then(m => ({ default: m.EmbedEntryLevel })));
const EmbedCity = lazy(() => import('./pages/data/EmbedCharts').then(m => ({ default: m.EmbedCity })));
const EmbedRemote = lazy(() => import('./pages/data/EmbedCharts').then(m => ({ default: m.EmbedRemote })));
const EmbedFpa = lazy(() => import('./pages/data/EmbedCharts').then(m => ({ default: m.EmbedFpa })));
const EmbedPpr = lazy(() => import('./pages/data/EmbedCharts').then(m => ({ default: m.EmbedPpr })));
const EmbedSkills = lazy(() => import('./pages/data/EmbedCharts').then(m => ({ default: m.EmbedSkills })));
const EmbedRole = lazy(() => import('./pages/data/EmbedCharts').then(m => ({ default: m.EmbedRole })));
const EmbedGovPrograms = lazy(() => import('./pages/data/EmbedCharts').then(m => ({ default: m.EmbedGovPrograms })));
const EmbedOpenDoor = lazy(() => import('./pages/data/EmbedCharts').then(m => ({ default: m.EmbedOpenDoor })));
const EmbedCredentials = lazy(() => import('./pages/data/EmbedCharts').then(m => ({ default: m.EmbedCredentials })));
const EmbedCampus = lazy(() => import('./pages/data/EmbedCharts').then(m => ({ default: m.EmbedCampus })));
const DataApi = lazy(() => import("./pages/data/DataApi"));

const queryClient = new QueryClient();

const AnalyticsWrapper = ({ children }: { children: React.ReactNode }) => {
  useAnalytics();
  // First-party events (src/lib/siteEvents.ts): one view per route change,
  // pathname only. The query string is left out on purpose.
  const { pathname } = useLocation();
  React.useEffect(() => { installSiteEvents(); }, []);
  React.useEffect(() => { siteEvents.view(pathname); }, [pathname]);
  return <>{children}</>;
};

// /search has no page of its own: the board's search is the home page, which
// reads q, title and location from the URL. Forward with the query string (and
// any utm params) intact, so newsletter links and the WebSite SearchAction
// target land on real results instead of a static "no jobs match" page.
const SearchRedirect = () => {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: "/", search, hash }} replace />;
};

// A client redirect that keeps the query string and hash (LINK-13). Plain
// <Navigate to="/x"> dropped them, so newsletter and email links to /jobs,
// /resume-tailor and /interview-prep lost their utm_* tags on arrival.
// (src/lib/attribution.ts reads the landing URL before any redirect, so the
// first touch survives either way; this keeps the tags visible to the page.)
const KeepQueryRedirect = ({ to }: { to: string }) => {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: to, search, hash }} replace />;
};

import { CreditWallProvider } from "./contexts/CreditWallContext";

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <BrowserRouter>
        {/* Sitewide social/description fallbacks; per-route <Helmet>s override them.
            These MUST mirror the data-rh="true" tags in index.html: those static
            copies serve scrapers that don't run JS, and Helmet clears them on
            mount, so without a fallback here a page that sets no og:title would
            end up with none at all. */}
        <Helmet>
          <meta
            name="description"
            content="Accounting and finance jobs read from employers' own career pages and checked hourly, with posted pay where the employer states it."
          />
          {/* Mirrors the data-rh robots tag in index.html. Helmet clears adopted
              tags on mount, so without this fallback a page that sets no robots
              directive would lose max-image-preview/max-snippet entirely. */}
          <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />
          {/* Image fallbacks mirror the data-rh image tags in index.html for the
              same reason — pages that set their own og:image (the /data pages)
              override these; every other page keeps the site card. */}
          <meta property="og:image" content="https://jobs.auditfriendly.co/og/social-share.png" />
          <meta name="twitter:image" content="https://jobs.auditfriendly.co/og/social-share.png" />
          <meta property="og:title" content="Accounting &amp; Finance Jobs | Audit Friendly" />
          <meta
            property="og:description"
            content="Accounting and finance jobs read from employers' own career pages and checked hourly, with posted pay where the employer states it."
          />
          <meta property="og:url" content="https://jobs.auditfriendly.co/" />
          <meta name="twitter:title" content="Accounting &amp; Finance Jobs | Audit Friendly" />
          <meta
            name="twitter:description"
            content="Accounting and finance jobs read from employers' own career pages and checked hourly, with posted pay where the employer states it."
          />
        </Helmet>
        <ScrollToTop />
        <GlobalNav />
        <AnalyticsWrapper>
          <CreditWallProvider>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Job seeker routes */}
              <Route path="/" element={<HomeV2 />} />
              {/* AuditFriendly Data (public data products) */}
              <Route path="/data" element={<DataHub />} />
              <Route path="/data/explorer" element={<MarketExplorer />} />
              <Route path="/data/methodology" element={<DataMethodology />} />
              <Route path="/data/press" element={<DataPress />} />
              <Route path="/data/license" element={<DataLicense />} />
              <Route path="/embed/explorer" element={<EmbedExplorer />} />
              <Route path="/embed/premium-jobs" element={<EmbedPremiumJobs />} />
              <Route path="/students" element={<StudentLanding />} />
              <Route path="/for-career-centers" element={<CareerCenters />} />
              <Route path="/data/early-career" element={<EarlyCareer />} />
              <Route path="/data/early-career/who-hires-students" element={<EarlyCareer fixedView="firms" />} />
              <Route path="/data/early-career/top-intern-employers" element={<EarlyCareer fixedView="top" />} />
              <Route path="/data/early-career/first-job-pay" element={<EarlyCareer fixedView="pay" />} />
              <Route path="/data/early-career/cpa-exam-support" element={<EarlyCareer fixedView="cpa" />} />
              <Route path="/data/early-career/sponsorship" element={<EarlyCareer fixedView="sponsorship" />} />
              <Route path="/data/early-career/cpa-training-offices" element={<EarlyCareer fixedView="ppr" />} />
              <Route path="/data/early-career/government-programs" element={<EarlyCareer fixedView="gov" />} />
              <Route path="/data/programs" element={<EarlyCareer fixedView="gov" />} />
              {/* One front door per audience: the programs table joined to the live
                  postings, employers and cities that concern that reader. */}
              <Route path="/data/programs/:slug" element={<AudiencePage />} />
              <Route path="/data/software" element={<SoftwareTracker />} />
              <Route path="/data/return-to-office" element={<RtoTracker />} />
              <Route path="/data/hiring-calendar" element={<HiringCalendar />} />
              <Route path="/data/time-to-close" element={<TimeToClose />} />
              <Route path="/data/vs-robert-half" element={<VsRobertHalf />} />
              <Route path="/embed/internships" element={<EmbedInternships />} />
              <Route path="/data/above-market" element={<AboveMarketFeed />} />
              <Route path="/data/cpa-firms" element={<CpaFirmTracker />} />
              <Route path="/data/cpa-premium" element={<CpaPremium />} />
              <Route path="/data/remote-first" element={<RemoteFirst />} />
              <Route path="/data/cpa-firm-jobs" element={<CpaFirmJobs />} />
              <Route path="/data/layoffs" element={<LayoffTracker />} />
              <Route path="/data/ontario-public-pay" element={<SunshineOntario />} />
              <Route path="/data/alberta-public-pay" element={<SunshineOntario province="AB" />} />
              <Route path="/data/bc-public-pay" element={<SunshineOntario province="BC" />} />
              <Route path="/data/newfoundland-public-pay" element={<SunshineOntario province="NL" />} />
              <Route path="/data/manitoba-public-pay" element={<SunshineOntario province="MB" />} />
              <Route path="/data/canada-public-pay" element={<PublicPayCompare />} />
              <Route path="/data/ai-companies" element={<AiCompanies />} />
              <Route path="/embed/ai-companies" element={<EmbedAiCompanies />} />
              <Route path="/data/posting-hygiene" element={<PostingHygiene />} />
              <Route path="/data/live" element={<DataLive />} />
              <Route path="/data/entry-level" element={<EntryLevel />} />
              <Route path="/data/open-door" element={<OpenDoor />} />
              <Route path="/data/open-door/:view" element={<OpenDoor />} />
              <Route path="/data/credentials" element={<Credentials />} />
              <Route path="/data/credentials/:view" element={<Credentials />} />
              <Route path="/data/campus" element={<CampusEmployerPage />} />
              <Route path="/data/campus/:slug" element={<CampusEmployerPage />} />
              <Route path="/data/cities" element={<CityPage />} />
              <Route path="/data/cities/:slug" element={<CityPage />} />
              <Route path="/data/cities/:slug/counsellors" element={<CityOnePager />} />
              <Route path="/data/fpa" element={<FpaPage />} />
              <Route path="/data/remote-us" element={<RemoteCountryPage country="US" />} />
              <Route path="/data/remote-ca" element={<RemoteCountryPage country="CA" />} />
              <Route path="/data/skills" element={<SkillsPage />} />
              <Route path="/embed/live" element={<EmbedLive />} />
              <Route path="/embed/remote-weekly" element={<EmbedRemoteWeekly />} />
              <Route path="/embed/nl/:widget" element={<EmbedNewsletterWidget />} />
              <Route path="/embed/recruiter-call-list" element={<EmbedCallList />} />
              <Route path="/embed/posting-hygiene" element={<EmbedPostingHygiene />} />
              <Route path="/data/curation" element={<Curation />} />
              <Route path="/embed/curation" element={<EmbedCuration />} />
              <Route path="/embed/layoffs" element={<EmbedLayoffs />} />
              <Route path="/go" element={<GoInterstitial />} />
              <Route path="/confirmed" element={<Confirmed />} />
              <Route path="/embed/cpa-premium" element={<EmbedCpaPremium />} />
              <Route path="/embed/remote-first" element={<EmbedRemoteFirst />} />
              <Route path="/embed/cpa-firm-pay" element={<EmbedCpaFirmPay />} />
              <Route path="/embed/cpa-firm-hiring" element={<EmbedCpaFirmHiring />} />
              <Route path="/embed/cpa-firms" element={<EmbedCpaFirms />} />
              <Route path="/embed/software" element={<EmbedSoftware />} />
              <Route path="/embed/return-to-office" element={<EmbedRto />} />
              <Route path="/embed/hiring-calendar" element={<EmbedHiringCalendar />} />
              <Route path="/embed/time-to-close" element={<EmbedTimeToClose />} />
              <Route path="/embed/vs-robert-half" element={<EmbedVsRobertHalf />} />
              <Route path="/embed/canada-public-pay" element={<EmbedPublicPayCompare />} />
              <Route path="/embed/public-pay" element={<EmbedProvincePay />} />
              <Route path="/embed/entry-level" element={<EmbedEntryLevel />} />
              <Route path="/embed/city" element={<EmbedCity />} />
              <Route path="/embed/remote" element={<EmbedRemote />} />
              <Route path="/embed/fpa" element={<EmbedFpa />} />
              <Route path="/embed/cpa-training-offices" element={<EmbedPpr />} />
              <Route path="/embed/skills" element={<EmbedSkills />} />
              <Route path="/embed/role" element={<EmbedRole />} />
              <Route path="/embed/government-programs" element={<EmbedGovPrograms />} />
              <Route path="/embed/open-door" element={<EmbedOpenDoor />} />
              <Route path="/embed/credentials" element={<EmbedCredentials />} />
              <Route path="/embed/campus" element={<EmbedCampus />} />
              <Route path="/data/api" element={<DataApi />} />
              {/* Public SEO landing pages for the career-portal tools */}
              <Route path="/tools/:slug" element={<ToolLandingPage />} />
              {/* Sales pages for the downloadable CSV products, and their hub */}
              <Route path="/lists" element={<ListsHub />} />
              <Route path="/lists/:slug" element={<ListLandingPage />} />
              <Route path="/v0-archive" element={<AuthProvider><RecruiterRedirectGuard><Layout><AllJobs /></Layout></RecruiterRedirectGuard></AuthProvider>} />
              <Route path="/jobs/preview" element={<AuthProvider><RecruiterRedirectGuard><Layout><JobDetailPreview /></Layout></RecruiterRedirectGuard></AuthProvider>} />
              <Route path="/jobs/:id" element={<AuthProvider><RecruiterRedirectGuard><Layout><JobDetailPage /></Layout></RecruiterRedirectGuard></AuthProvider>} />
              <Route path="/jobs" element={<KeepQueryRedirect to="/" />} />
              <Route path="/about" element={<Layout><About /></Layout>} />
              <Route path="/updates" element={<AuthProvider><Layout><Updates /></Layout></AuthProvider>} />
              <Route path="/learn/conferences" element={<LearnConferences />} />
              <Route path="/learn/cpe" element={<LearnCpe />} />
              <Route path="/learn" element={<Navigate to="/learn/cpe" replace />} />
              <Route path="/pricing" element={<AuthProvider><Layout><Pricing /></Layout></AuthProvider>} />
              {/* Search Sprint: $29 once, 30 days, nothing renews (migration 20261018120000). */}
              <Route path="/sprint" element={<AuthProvider><Layout><SprintLanding /></Layout></AuthProvider>} />
              <Route path="/sprint/success" element={<AuthProvider><Layout><RequireAuth><SprintSuccess /></RequireAuth></Layout></AuthProvider>} />
              <Route path="/sprint/signals" element={<AuthProvider><Layout><RequireAuth><SprintSignals /></RequireAuth></Layout></AuthProvider>} />
              <Route path="/search" element={<SearchRedirect />} />
              <Route path="/salary-data" element={<KeepQueryRedirect to="/dashboard/salary-research" />} />
              <Route path="/resume-tailor" element={<KeepQueryRedirect to="/dashboard/resume-tailor" />} />
              <Route path="/linkedin-optimizer" element={<AuthProvider><RecruiterRedirectGuard><Layout><LinkedInOptimizer /></Layout></RecruiterRedirectGuard></AuthProvider>} />
              {/* The legacy /interview-prep page called interview-prep-ai on every
                  question and every mock answer, 8 credits a call (a five-answer
                  mock cost ~88 credits against an advertised 8 a packet). The
                  dashboard tool is the product the pricing page describes. */}
              <Route path="/interview-prep" element={<KeepQueryRedirect to="/dashboard/interview-prep" />} />
              <Route path="/ats-job-search" element={<AuthProvider><RecruiterRedirectGuard><Layout><ATSJobSearch /></Layout></RecruiterRedirectGuard></AuthProvider>} />

              <Route path="/get-started" element={<AuthProvider><Layout><GetStarted /></Layout></AuthProvider>} />

              {/* SEO Landing Pages — registry lives in src/seo/landingPages.ts */}
              {/* The 11 canonical-role pages render in the Data Desk layout;
                  the older remote-* landings keep the job-board template. */}
              {/* Every landing path now renders the Data Desk layout; the old
                  job-board template was retired once the remote-* pages moved
                  across, so ROLE_PAGE_PATHS covers the whole registry. */}
              {LANDING_PATHS.map(path => (
                <Route key={path} path={path} element={<RolePage />} />
              ))}

              <Route path="/candidates" element={<AuthProvider><Candidates /></AuthProvider>} />
              <Route path="/settings/*" element={<Navigate to="/dashboard/settings" replace />} />
              <Route path="/dashboard/*" element={<AuthProvider><RecruiterRedirectGuard><Dashboard /></RecruiterRedirectGuard></AuthProvider>} />
              <Route path="/auth" element={<AuthProvider><RecruiterRedirectGuard><Layout><Auth /></Layout></RecruiterRedirectGuard></AuthProvider>} />
              <Route path="/sign-in" element={<AuthProvider><SignIn /></AuthProvider>} />
              {/* LINK-07: /login and /billing were linked from the app but never existed. */}
              <Route path="/login" element={<KeepQueryRedirect to="/sign-in" />} />
              <Route path="/billing" element={<KeepQueryRedirect to="/pricing" />} />
              <Route path="/sign-up" element={<AuthProvider><SignUp /></AuthProvider>} />
              <Route path="/auth/check-email" element={<AuthProvider><CheckEmail /></AuthProvider>} />
              <Route path="/auth/callback" element={<AuthProvider><AuthCallback /></AuthProvider>} />
              <Route path="/auth/verify-email" element={<AuthProvider><VerifyEmail /></AuthProvider>} />
              <Route path="/auth/forgot-password" element={<AuthProvider><ForgotPassword /></AuthProvider>} />
              <Route path="/auth/reset-password" element={<AuthProvider><ResetPassword /></AuthProvider>} />

              {/* Recruiter routes */}
              {/* /recruiter retired — client redirect to /post-a-job. Add a 301 at the
                  hosting/edge layer (Cloudflare/Vercel) to preserve SEO equity. */}
              <Route path="/recruiter" element={<Navigate to="/post-a-job" replace />} />
              <Route path="/post-a-job" element={<Layout><RecruiterLandingPage /></Layout>} />
              <Route path="/recruiter/auth" element={<Navigate to="/recruiter/sign-in" replace />} />
              <Route path="/recruiter/sign-in" element={<RecruiterAuthProvider><RecruiterSignIn /></RecruiterAuthProvider>} />
              <Route path="/recruiter/post" element={<RecruiterAuthProvider><RecruiterPost /></RecruiterAuthProvider>} />
              <Route path="/recruiter/checkout" element={<RecruiterAuthProvider><RecruiterCheckout /></RecruiterAuthProvider>} />
              <Route path="/recruiter/checkout/success" element={<RecruiterAuthProvider><RecruiterCheckoutSuccess /></RecruiterAuthProvider>} />
              <Route path="/recruiter/dashboard" element={<RecruiterAuthProvider><RecruiterDashboardV1 /></RecruiterAuthProvider>} />
              <Route path="/recruiter/dashboard/settings" element={<RecruiterAuthProvider><RecruiterSettingsV1 /></RecruiterAuthProvider>} />
              <Route path="/recruiter/dashboard/billing" element={<RecruiterAuthProvider><RecruiterBillingV1 /></RecruiterAuthProvider>} />
              <Route path="/recruiter/dashboard/edit/:id" element={<RecruiterAuthProvider><RecruiterEditListing /></RecruiterAuthProvider>} />

              {/* Internal routes - no auth required */}
              <Route path="/internal/social-insights" element={<Layout><SocialInsights /></Layout>} />
              <Route path="/internal/backfill-locations" element={<Layout><BackfillLocations /></Layout>} />
              <Route path="/internal/job-intelligence" element={<AuthProvider><Layout><JobIntelligence /></Layout></AuthProvider>} />
              <Route path="/internal/job-ads" element={
                <AuthProvider>
                  <RequireAuth>
                    <Layout><JobListAdsAdmin /></Layout>
                  </RequireAuth>
                </AuthProvider>
              } />
              <Route path="/internal/newsletter-generator" element={
                <AuthProvider>
                  <RequireAuth>
                    <NewsletterGenerator />
                  </RequireAuth>
                </AuthProvider>
              } />
              <Route path="/internal/bulk-upload" element={<Layout><BulkJobUpload /></Layout>} />

              {/* Beta */}
              <Route path="/beta" element={<BetaHome />} />
              <Route path="/beta/jobs/:id" element={<BetaJobDetail />} />

              {/* TODO: remove /v2 alias after 2026-05-30 (30-day grace for in-flight bookmarks). */}
              <Route path="/v2" element={<HomeV2 />} />

              {/* Admin — gated by has_role('admin'); no Layout, no RecruiterRedirectGuard. */}
              <Route
                path="/admin"
                element={
                  <AuthProvider>
                    <RequireAdmin>
                      <AdminLayout />
                    </RequireAdmin>
                  </AuthProvider>
                }
              >
                <Route index element={<AdminStatsHome />} />
                <Route path="health" element={<AdminHealth />} />
                <Route path="usage" element={<AdminUsage />} />
                <Route path="newsletters" element={<NewslettersHub />} />
                <Route path="newsletters/:slug" element={<NewsletterDetail />} />
                <Route path="newsletters/:slug/:issueId" element={<NewsletterEditor />} />
                <Route path="studio" element={<ContentStudio />} />
                <Route path="insights" element={<InsightsConsole />} />
                <Route path="library-videos" element={<AdminLibraryVideos />} />
                {/* /admin/design never existed; it is where muscle memory goes
                    looking for the content builder, so send it to the Studio. */}
                <Route path="design" element={<Navigate to="/admin/studio" replace />} />
              </Route>

              {/* Legal */}
              <Route path="/terms" element={<Layout><Terms /></Layout>} />
              {/* Linked from the recruiter checkout footer; the refund terms live in the ToS (§6.3, §6.7, §7.5). */}
              <Route path="/refund-policy" element={<Navigate to="/terms" replace />} />
              <Route path="/privacy" element={<Layout><Privacy /></Layout>} />

              {/* Public routes */}
              <Route path="/unsubscribe" element={<Layout><Unsubscribe /></Layout>} />
              <Route path="/briefing/answer" element={<Layout><BriefingAnswer /></Layout>} />
              {/* Buyer's copy of the Close Prompt Playbook; Stripe Payment Link redirects here. */}
              {/* Close Prompt Playbook sales page; it carries its own header and footer. */}
              <Route path="/playbook" element={<PlaybookPage />} />
              <Route path="/playbook/access" element={<Layout><PlaybookAccess /></Layout>} />
              {/* AuditFriendly Library marketplace: prompts, skills, workflows and agents (library/README.md).
                  Catalog from src/data/library (library/scripts/build_catalog.py); checkout and paid content
                  through create-library-checkout and library-access. */}
              <Route path="/library" element={<LibraryLayout><LibraryHome /></LibraryLayout>} />
              <Route path="/library/pros" element={<LibraryLayout><LibraryPros /></LibraryLayout>} />
              <Route path="/library/access" element={<LibraryLayout><LibraryAccess /></LibraryLayout>} />
              {/* Upgrade credit link from a Library receipt: what you paid counts toward the bundle. */}
              <Route path="/library/upgrade" element={<LibraryLayout><LibraryUpgrade /></LibraryLayout>} />
              <Route path="/library/:slug" element={<LibraryLayout><LibraryItemPage /></LibraryLayout>} />
              <Route path="/small-business" element={<LibraryLayout><SmallBusiness /></LibraryLayout>} />
              {/* Library landing pages, one job each (LIB-01; src/lib/library/guides.ts). */}
              <Route path="/ai-month-end-close" element={<LibraryLayout><AiMonthEndClose /></LibraryLayout>} />
              <Route path="/year-end-books-for-your-cpa" element={<LibraryLayout><YearEndForCpa /></LibraryLayout>} />
              <Route path="/claude-skills-for-accountants" element={<LibraryLayout><ClaudeSkillsForAccountants /></LibraryLayout>} />
              <Route path="/test-email" element={<Layout><TestEmail /></Layout>} />
              <Route path="*" element={<Layout><NotFound /></Layout>} />
            </Routes>
          </Suspense>
          </CreditWallProvider>
        </AnalyticsWrapper>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
